/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TalkingPoint } from '../../../interfaces/talking-point.interface';
import {
  FaClock,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi2';

interface TalkingPointsViewProps {
  data: TalkingPoint[] | any;
}

const TalkingPointsView: React.FC<TalkingPointsViewProps> = ({ data }) => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  // Handle different data structures
  const talkingPoints = Array.isArray(data)
    ? data
    : (data?.talkingPoints && Array.isArray(data.talkingPoints))
      ? data.talkingPoints
      : [];

  const toggleCard = (id: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // If no talking points are provided, display a fallback message
  if (!talkingPoints || talkingPoints.length === 0) {
    return (
      <div className="w-full p-4 sm:p-6">
        <div className="w-full max-h-[80vh] overflow-y-auto rounded-lg bg-gray-50 p-8 sm:p-12 text-center border border-gray-200">
          <div className="flex flex-col items-center justify-center space-y-3">
            <HiSparkles className="text-3xl text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-700">No Talking Points Available</h3>
            <p className="text-sm text-gray-500 max-w-md">
              Talking points will appear here once they are generated for your session.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                {talkingPoints.length} talking {talkingPoints.length === 1 ? 'point' : 'points'}
              </p>
            </div>
          </div>
        </div>

        {/* Talking Points List */}
        <div className="space-y-3">
          {talkingPoints.map((talkingPoint: any, index: number) => {
            const isExpanded = expandedCards.has(talkingPoint.id || index);

            return (
              <motion.div
                key={talkingPoint.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden"
              >
                {/* Card Header */}
                <div
                  className="p-4 sm:p-5 cursor-pointer flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCard(talkingPoint.id || index)}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-gray-600">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        {talkingPoint.topic && (
                          <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            {talkingPoint.topic}
                          </span>
                        )}
                        {talkingPoint.type && (
                          <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                            {talkingPoint.type}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>
                          {talkingPoint.talkingPoints?.length || 0} {talkingPoint.talkingPoints?.length === 1 ? 'detail' : 'details'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-gray-400">
                    {isExpanded ? (
                      <FaChevronUp className="text-sm" />
                    ) : (
                      <FaChevronDown className="text-sm" />
                    )}
                  </div>
                </div>

                {/* Card Content */}
                <AnimatePresence>
                  {isExpanded && talkingPoint.talkingPoints && Array.isArray(talkingPoint.talkingPoints) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 sm:px-5 sm:pb-5 bg-gray-50 border-t border-gray-100">
                        <div className="space-y-3 pt-3">
                          {talkingPoint.talkingPoints.map((tp: any, tpIndex: number) => (
                            <div
                              key={tpIndex}
                              className="bg-white rounded-md p-4 border border-gray-200"
                            >
                              {/* Section Header */}
                              <div className="mb-2">
                                <h4 className="text-sm font-semibold text-gray-800 mb-1">
                                  {tp.section}
                                </h4>
                                {tp.time && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <FaClock className="text-[10px]" />
                                    <span>{tp.time}</span>
                                  </div>
                                )}
                              </div>

                              {/* Instructions */}
                              {tp.instructions && (
                                <div className="mb-2 p-2.5 rounded-md bg-blue-50 border border-blue-100">
                                  <p className="text-xs text-gray-700 leading-relaxed">
                                    {tp.instructions}
                                  </p>
                                </div>
                              )}

                              {/* Content */}
                              {tp.content && (
                                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                                  {tp.content}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TalkingPointsView;